/** @jsxImportSource @emotion/react */

import { InfoOutlined } from '@mui/icons-material'
import { Box, Skeleton, Tooltip, Typography, useTheme } from '@mui/material'
import { useAtomValue } from 'jotai'
import { currencySymbolAtom } from '@app/state/clientsAtom'
import type { AalReason } from '@app/hooks/useAal'
import { formatAalBand, reasonMessage } from './AalStat.format'
import {
  aalStatInfoIcon,
  aalStatLabel,
  aalStatNotAvailable,
  aalStatRow,
  aalStatValue,
} from './AalStat.styles'

export interface AalStatProps {
  aal_min: number | null | undefined
  aal_max: number | null | undefined
  reason?: AalReason
  isLoading?: boolean
  label?: string
  /** Optional currency override; otherwise pulled from `currencySymbolAtom`. */
  currencySymbol?: string
}

/**
 * Renders one of three states based on inputs:
 *   - Loading skeleton when `isLoading`.
 *   - Value band (e.g. `$1.2M – $2.4M / yr`) when both bounds are finite.
 *   - "N/A" with an info-icon tooltip explaining the `reason`, otherwise.
 *
 * Currency comes from `currencySymbolAtom` unless overridden via prop.
 */
export const AalStat = ({
  aal_min,
  aal_max,
  reason,
  isLoading,
  label = 'Event Set AAL',
  currencySymbol,
}: AalStatProps) => {
  const theme = useTheme()
  const fallbackCurrency = useAtomValue(currencySymbolAtom)
  const symbol = currencySymbol ?? fallbackCurrency

  const hasBand =
    aal_min != null && aal_max != null && Number.isFinite(aal_min) && Number.isFinite(aal_max)

  return (
    <Box css={aalStatRow({ theme })}>
      <Typography component="span" css={aalStatLabel}>
        {label}:
      </Typography>
      {isLoading ? (
        <Skeleton variant="text" width={120} />
      ) : hasBand ? (
        <Typography component="span" css={aalStatValue}>
          {formatAalBand(aal_min, aal_max, symbol)} / yr
        </Typography>
      ) : (
        <Typography component="span" css={aalStatNotAvailable({ theme })}>
          N/A
        </Typography>
      )}
      {!isLoading && (
        <Tooltip title={reasonMessage(reason)}>
          <InfoOutlined css={aalStatInfoIcon({ theme })} />
        </Tooltip>
      )}
    </Box>
  )
}

export default AalStat
